/**
 * Google Analytics React Hook
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { analytics } from '../services/analyticsService';
import { log } from '../utils/logger';

const PAGE_TITLES: Record<string, { zh: string; en: string }> = {
  '/': {
    zh: 'AtlasSlicer 2D - 图集打包、智能切图与动效质检工作台',
    en: 'AtlasSlicer 2D - Texture Packing, Sprite Slicing & Animation QC Workstation',
  },
  '/features': {
    zh: '核心功能特性 - AtlasSlicer 2D',
    en: 'Features - AtlasSlicer 2D',
  },
  '/pricing': {
    zh: '终身授权定价 - AtlasSlicer 2D',
    en: 'Pricing - AtlasSlicer 2D',
  },
  '/download': {
    zh: '客户端下载 - AtlasSlicer 2D',
    en: 'Download Client - AtlasSlicer 2D',
  },
  '/refund-policy': {
    zh: '退款政策 - AtlasSlicer 2D',
    en: 'Refund Policy - AtlasSlicer 2D',
  },
  '/privacy-policy': {
    zh: '隐私政策 - AtlasSlicer 2D',
    en: 'Privacy Policy - AtlasSlicer 2D',
  },
  '/user-agreement': {
    zh: '用户协议 - AtlasSlicer 2D',
    en: 'User Agreement - AtlasSlicer 2D',
  },
};

/**
 * 自动追踪页面浏览的 Hook
 */
export function usePageTracking() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    // 动态同步页面标题与 GA 浏览上报
    const timer = setTimeout(() => {
      const isZh = i18n.language?.startsWith('zh');
      const routeConfig = PAGE_TITLES[location.pathname];
      const pageTitle = routeConfig
        ? (isZh ? routeConfig.zh : routeConfig.en)
        : document.title;

      document.title = pageTitle;
      const path = location.pathname + location.search;

      analytics.trackPageView(path, pageTitle);
      log.debug('页面浏览追踪:', { path, title: pageTitle });
    }, 100);

    return () => clearTimeout(timer);
  }, [location, i18n.language]);
}

/**
 * 手动页面追踪 Hook
 */
export function useManualPageTracking() {
  const trackPage = (path?: string, title?: string) => {
    analytics.trackPageView(path, title);
  };

  return { trackPage };
}

/**
 * 事件追踪 Hook
 */
export function useEventTracking() {
  const trackEvent = (eventName: string, params?: Record<string, any>) => {
    analytics.trackEvent(eventName, params);
  };

  const trackLogin = (method?: string) => {
    analytics.trackLogin(method);
  };

  const trackSignUp = (method?: string) => {
    analytics.trackSignUp(method);
  };

  const trackLogout = () => {
    analytics.trackLogout();
  };

  const trackOAuthLogin = {
    attempt: (provider: string) => analytics.trackOAuthLoginAttempt(provider),
    success: (provider: string) => analytics.trackOAuthLoginSuccess(provider),
    error: (provider: string, error: string) => analytics.trackOAuthLoginError(provider, error),
  };

  const trackEcommerce = {
    viewPricing: () => analytics.trackViewPricing(),
    beginCheckout: (currency?: string, value?: number) => analytics.trackBeginCheckout(currency, value),
    purchase: (transactionId: string, value: number, currency?: string) => 
      analytics.trackPurchase(transactionId, value, currency),
    cancelSubscription: (reason?: string) => analytics.trackSubscriptionCancel(reason),
  };

  const trackEngagement = {
    viewFeatures: () => analytics.trackViewFeatures(),
    viewProfile: () => analytics.trackProfileView(),
  };

  const trackError = (error: string, category?: string) => {
    analytics.trackError(error, category);
  };

  return {
    trackEvent,
    trackLogin,
    trackSignUp,
    trackLogout,
    trackOAuthLogin,
    trackEcommerce,
    trackEngagement,
    trackError,
  };
}

/**
 * 用户属性设置 Hook
 */
export function useUserTracking() {
  const setUserProperties = (properties: Record<string, any>) => {
    analytics.setUserProperties(properties);
  };

  const setUserId = (userId: string) => {
    analytics.setUserId(userId);
  };

  return {
    setUserProperties,
    setUserId,
  };
}
